import React from "react";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Typography, Button } from "@mui/material";
import { Block } from "@mui/icons-material";
import { usePermissions } from "../../hooks/usePermissions";

/**
 * Protects routes by permission. Renders children (Outlet) only if user has required permission.
 * @param {string} requiredPermission - Permission slug required to access the route
 * @param {string[]} requiredAnyPermission - Alternative: user needs ANY of these permissions
 * @param {string[]} disallowedRoles - If the user's role slug matches any (case-insensitive), deny access
 */
const PermissionBasedRoute = ({
  requiredPermission,
  requiredAnyPermission,
  disallowedRoles = [],
}) => {
  const { hasVisiblePermission, hasAnyPermission, isLoading } = usePermissions();
  const { user } = useSelector((state) => state.auth);

  if (!user) {
    return null;
  }

  const roleSlug = String(user?.role?.slug || user?.role || "")
    .trim()
    .toLowerCase();
  if (
    Array.isArray(disallowedRoles) &&
    disallowedRoles.length > 0 &&
    disallowedRoles.some((r) => String(r).trim().toLowerCase() === roleSlug)
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          textAlign: "center",
          p: 3,
        }}
      >
        <Block sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          You don&apos;t have permission to access this page.
        </Typography>
        <Button variant="contained" href="/dashboard">
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  let hasAccess = false;
  if (requiredPermission) {
    hasAccess = hasVisiblePermission(requiredPermission);
  } else if (Array.isArray(requiredAnyPermission) && requiredAnyPermission.length > 0) {
    hasAccess = hasAnyPermission(requiredAnyPermission);
  } else {
    hasAccess = true;
  }

  if (!hasAccess) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          textAlign: "center",
          p: 3,
        }}
      >
        <Block sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          You don&apos;t have permission to access this page.
        </Typography>
        <Button variant="contained" href="/dashboard">
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  return <Outlet />;
};

export default PermissionBasedRoute;
